import _find from 'lodash-es/find';

import {
  type AclsCreateBulkACLsSchema,
  httpDeleteAclByObjectTypeContent,
  httpDeleteGroupsByGroupIdAclByObjectTypeByObjectKey,
  httpDeleteUsersByUserIdAclByObjectTypeByObjectKey,
  httpGetAclByObjectTypeByObjectKey,
  httpGetAclByObjectTypeByObjectKeyPermissions,
  httpPutAclByObjectType,
  httpPutAclByObjectTypeContent,
} from '@iconik-io/iconik-api/src/services/acls';
import { isPermission } from '@iconik-io/shared-lib/src/redux/entities/normalized/search_item';

import type { AppDispatch, GetState } from '../../store/types';
import { allGrantedPermissions, defaultPermissions } from '../../types/common';
import { successMessage } from '../messages/thunks';
import { ACL_TYPE_PROPAGATED } from './constants';
import { getCommonAclsSlice } from './slice';
import type {
  AccessGrant,
  AclType,
  AddToGroupPayload,
  AddToUserPayload,
  CommonAclsPrefix,
} from './types';

export function getEntityAcls(prefix: CommonAclsPrefix, id: string) {
  return (dispatch: AppDispatch): Promise<unknown> =>
    dispatch(
      httpGetAclByObjectTypeByObjectKey(prefix, id, { exclude_deleted: true }),
    ).then((response) =>
      dispatch(
        getCommonAclsSlice(prefix).actions.aclsSet({ id, acl: response.body }),
      ),
    );
}

export function clearEntityAcls(prefix: CommonAclsPrefix) {
  return (dispatch: AppDispatch): void => {
    dispatch(getCommonAclsSlice(prefix).actions.aclsClear());
  };
}

export function getEntityCurrentAcls(prefix: CommonAclsPrefix, id: string) {
  return (dispatch: AppDispatch, getState: GetState): Promise<unknown> => {
    const isAdmin = getState().authReducer.userData.is_admin;
    return dispatch(
      httpGetAclByObjectTypeByObjectKeyPermissions(prefix, id),
    ).then((response) => {
      // An absent list means the request told us nothing; an empty one is a
      // real answer, and an admin is still granted everything below.
      if (!response.body.permissions) {
        return;
      }
      const responsePermissions =
        response.body.permissions.filter(isPermission);
      const permissions = { ...defaultPermissions };
      responsePermissions.forEach((responsePermission) => {
        permissions[responsePermission] = true;
      });
      dispatch(
        getCommonAclsSlice(prefix).actions.aclsSetCurrent({
          id,
          permissions: isAdmin ? allGrantedPermissions : permissions,
        }),
      );
    });
  };
}

export type AddEntityAclsData = AccessGrant & {
  type: AclType;
};

const PROPAGATING_COLLECTIONS_OBJECT_TYPE = '_propagating_collections';

export function addEntityAcls(
  aclsType: CommonAclsPrefix,
  ids: string[],
  data: AddEntityAclsData,
) {
  return (dispatch: AppDispatch): Promise<unknown> => {
    const objectType =
      data.type === ACL_TYPE_PROPAGATED
        ? PROPAGATING_COLLECTIONS_OBJECT_TYPE
        : aclsType;

    const userIds = data.user_ids ?? [];
    const groupIds = data.group_ids ?? [];

    return dispatch(
      httpPutAclByObjectType(objectType, {
        object_keys: ids,
        user_ids: userIds,
        group_ids: groupIds,
        permissions: data.permissions,
      }),
    ).then(() => {
      ids.forEach((id) => {
        userIds.forEach((userId) => {
          const payload: AddToUserPayload = {
            id,
            permissions: data.permissions,
            user_id: userId,
            type: data.type,
          };
          dispatch(getCommonAclsSlice(aclsType).actions.aclsAddToUser(payload));
        });
        groupIds.forEach((groupId) => {
          const payload: AddToGroupPayload = {
            id,
            permissions: data.permissions,
            group_id: groupId,
            type: data.type,
          };
          dispatch(
            getCommonAclsSlice(aclsType).actions.aclsAddToGroup(payload),
          );
        });
      });
      dispatch(successMessage({ text: 'Permissions updated' }));
    });
  };
}

export function deleteEntityAcls(
  aclsType: CommonAclsPrefix,
  aclsObjectId: string,
  profileId: string,
  type: AclType,
) {
  return (dispatch: AppDispatch, getState: GetState): Promise<void> => {
    const entityAcls = getState().acls[aclsType];
    const users = entityAcls.entityUsersAcls[aclsObjectId] ?? [];
    const groups = entityAcls.entityGroupsAcls[aclsObjectId] ?? [];

    const objectType =
      type === ACL_TYPE_PROPAGATED
        ? PROPAGATING_COLLECTIONS_OBJECT_TYPE
        : aclsType;

    const user = _find(users, { user_id: profileId });
    if (user) {
      return dispatch(
        httpDeleteUsersByUserIdAclByObjectTypeByObjectKey(
          profileId,
          objectType,
          aclsObjectId,
        ),
      ).then(() => {
        dispatch(successMessage({ text: 'Permissions updated' }));
        dispatch(
          getCommonAclsSlice(aclsType).actions.aclsDeleteFromUser({
            id: aclsObjectId,
            user_id: profileId,
            type,
          }),
        );
      });
    }

    const group = _find(groups, { group_id: profileId });
    if (group) {
      return dispatch(
        httpDeleteGroupsByGroupIdAclByObjectTypeByObjectKey(
          profileId,
          objectType,
          aclsObjectId,
        ),
      ).then(() => {
        dispatch(successMessage({ text: 'Permissions updated' }));
        dispatch(
          getCommonAclsSlice(aclsType).actions.aclsDeleteFromGroup({
            id: aclsObjectId,
            group_id: profileId,
            type,
          }),
        );
      });
    }

    return Promise.resolve();
  };
}

const bulkOperationMessage =
  'Permissions will be updated. This operation can take some time';

export type ApplyEntityAclsData = {
  user_ids: string[];
  group_ids: string[];
  permissions: string[];
};

export function applyEntityAclsBulk(
  aclsType: CommonAclsPrefix,
  ids: string[],
  data: ApplyEntityAclsData,
) {
  return (dispatch: AppDispatch): Promise<unknown> => {
    const update: AclsCreateBulkACLsSchema = {
      object_ids: ids,
      permissions: data.permissions,
      include_assets: true,
      include_collections: true,
      user_ids: data.user_ids,
      group_ids: data.group_ids,
    };
    return dispatch(httpPutAclByObjectTypeContent(aclsType, update)).then(() =>
      dispatch(successMessage({ text: bulkOperationMessage })),
    );
  };
}

export function deleteEntityAclsBulk(
  aclsType: CommonAclsPrefix,
  aclsObjectId: string,
  profileId: string,
) {
  return (dispatch: AppDispatch, getState: GetState): Promise<void> => {
    const entityAcls = getState().acls[aclsType];
    const users = entityAcls.entityUsersAcls[aclsObjectId] ?? [];
    const groups = entityAcls.entityGroupsAcls[aclsObjectId] ?? [];

    const user = _find(users, { user_id: profileId });
    if (user) {
      return dispatch(
        httpDeleteAclByObjectTypeContent(aclsType, {
          object_ids: [aclsObjectId],
          include_assets: true,
          include_collections: true,
          user_ids: [profileId],
        }),
      ).then(() => {
        dispatch(successMessage({ text: bulkOperationMessage }));
      });
    }

    const group = _find(groups, { group_id: profileId });
    if (group) {
      return dispatch(
        httpDeleteAclByObjectTypeContent(aclsType, {
          object_ids: [aclsObjectId],
          include_assets: true,
          include_collections: true,
          group_ids: [profileId],
        }),
      ).then(() => {
        dispatch(successMessage({ text: bulkOperationMessage }));
      });
    }

    return Promise.resolve();
  };
}

export function aclsSetEditByType(
  aclsType: CommonAclsPrefix,
  id: string | undefined,
) {
  return (dispatch: AppDispatch): void => {
    if (!aclsType) {
      return;
    }
    dispatch(getCommonAclsSlice(aclsType).actions.aclsSetEdit(id));
  };
}
